import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import {
  CheckCircle,
  Error,
  SwapHoriz,
  ExpandMore,
  Description,
  AttachFile,
} from '@mui/icons-material'
import { UrlReplacementJob, NodeChange, LeafChange } from './types'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { createIdMap, NodeIdMap } from '../version_mapping/utility'
import { openNode, openNodeOnTKatBuilder } from '../utility/TreeNavigation'

/**
 * Renders the results of a URL replacement job.
 * Displays summary statistics and detailed tables of node and leaf changes.
 */
export function UrlReplacementResponse({
  result,
}: {
  result: UrlReplacementJob
}) {
  const { data, error } = result

  // Extract tree info
  const tree_id = data?.data?.tree_id
  const version_id = data?.data?.version_id

  // Get the tree and create the ID map for navigation
  const { tree } = useGetTreeById({
    treeId: tree_id,
    versionId: version_id,
  })

  const idMap = useMemo(() => {
    return tree ? createIdMap(tree) : undefined
  }, [tree])

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : JSON.stringify(error)

    return (
      <Box padding="10px">
        <Alert severity="error">
          <strong>ERROR:</strong> {errorMessage}
        </Alert>
      </Box>
    )
  }

  const { node_changes, leaf_changes, summary } = data.data
  const errors = data.errors || []

  return (
    <Box>
      <ReplacementSummary summary={summary} />

      {errors.length > 0 && (
        <Box padding="10px">
          <Alert severity="error">
            <Typography variant="subtitle2">
              {errors.length} Error{errors.length !== 1 ? 's' : ''} Occurred:
            </Typography>
            {errors.map((err, idx) => (
              <Box key={idx} paddingTop="5px">
                <Typography variant="body2">{err}</Typography>
              </Box>
            ))}
          </Alert>
        </Box>
      )}

      {node_changes.length > 0 && (
        <NodeChangesTable nodeChanges={node_changes} idMap={idMap} />
      )}

      {leaf_changes.length > 0 && (
        <LeafChangesTable leafChanges={leaf_changes} />
      )}

      {node_changes.length === 0 && leaf_changes.length === 0 && (
        <Box padding="10px">
          <Alert severity="info">No URL replacements were made.</Alert>
        </Box>
      )}
    </Box>
  )
}

function ReplacementSummary({
  summary,
}: {
  summary: UrlReplacementJob['data']['data']['summary']
}) {
  return (
    <Box padding="10px">
      <Typography variant="h6" gutterBottom>
        Replacement Summary
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
        <Chip
          icon={<SwapHoriz />}
          label={`${summary.total_urls_to_replace} URLs to Replace`}
          color="default"
          size="small"
        />
        <Chip
          icon={<CheckCircle />}
          label={`${summary.urls_found} Found`}
          color="success"
          size="small"
        />
        {summary.urls_not_found > 0 && (
          <Chip
            icon={<Error />}
            label={`${summary.urls_not_found} Not Found`}
            color="warning"
            size="small"
          />
        )}
        <Chip
          icon={<Description />}
          label={`${summary.nodes_updated} Nodes Updated`}
          color="primary"
          size="small"
        />
        <Chip
          icon={<AttachFile />}
          label={`${summary.leaves_updated} Leaves Updated`}
          color="primary"
          size="small"
        />
        <Chip
          icon={<CheckCircle />}
          label={`${summary.successful_changes} Successful`}
          color="success"
          size="small"
        />
        {summary.failed_changes > 0 && (
          <Chip
            icon={<Error />}
            label={`${summary.failed_changes} Failed`}
            color="error"
            size="small"
          />
        )}
      </Stack>
    </Box>
  )
}

function NodeChangesTable({
  nodeChanges,
  idMap,
}: {
  nodeChanges: NodeChange[]
  idMap?: NodeIdMap
}) {
  const hasNavigation = !!idMap

  const columns: Column<NodeChange>[] = [
    {
      title: 'Node ID',
      rowContent: ({ node_id }) => {
        if (!hasNavigation || !idMap) {
          return (
            <Typography variant="body2" fontFamily="monospace">
              {node_id.slice(0, 8)}...
            </Typography>
          )
        }

        const nodeInfo = idMap[node_id]
        if (!nodeInfo) {
          return (
            <Typography variant="body2" fontFamily="monospace">
              {node_id.slice(0, 8)}...
            </Typography>
          )
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={() => openNode(nodeInfo.node, idMap)}
            >
              Kiddom
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openNodeOnTKatBuilder(nodeInfo.node)}
            >
              TKat
            </Button>
          </Stack>
        )
      },
    },
    {
      title: 'Field',
      rowContent: ({ field }) => (
        <Typography variant="body2" fontFamily="monospace">
          {field}
        </Typography>
      ),
    },
    {
      title: 'Previous URL',
      rowContent: ({ previous_url }) => (
        <Box maxWidth="300px" overflow="hidden" textOverflow="ellipsis">
          <Typography variant="body2" noWrap title={previous_url}>
            {previous_url}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'New URL',
      rowContent: ({ new_url }) => (
        <Box maxWidth="300px" overflow="hidden" textOverflow="ellipsis">
          <Typography variant="body2" noWrap title={new_url}>
            {new_url}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Occurrences',
      rowContent: ({ occurrences }) => (
        <Typography variant="body2">{occurrences}</Typography>
      ),
    },
    {
      title: 'Status',
      rowContent: ({ success, error }) =>
        success ? (
          <Chip
            label="Success"
            color="success"
            size="small"
            icon={<CheckCircle />}
          />
        ) : (
          <Chip
            label={error || 'Failed'}
            color="error"
            size="small"
            icon={<Error />}
          />
        ),
    },
  ]

  return (
    <Box padding="10px">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Node Changes ({nodeChanges.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table title="" items={nodeChanges} columns={columns} />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

function LeafChangesTable({ leafChanges }: { leafChanges: LeafChange[] }) {
  const columns: Column<LeafChange>[] = [
    {
      title: 'Leaf ID',
      rowContent: ({ leaf_id }) => (
        <Typography variant="body2" fontFamily="monospace">
          {leaf_id.slice(0, 8)}...
        </Typography>
      ),
    },
    {
      title: 'Field',
      rowContent: ({ field }) => (
        <Typography variant="body2" fontFamily="monospace">
          {field}
        </Typography>
      ),
    },
    {
      title: 'Previous URL',
      rowContent: ({ previous_url }) => (
        <Box maxWidth="300px" overflow="hidden" textOverflow="ellipsis">
          <Typography variant="body2" noWrap title={previous_url}>
            {previous_url}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'New URL',
      rowContent: ({ new_url }) => (
        <Box maxWidth="300px" overflow="hidden" textOverflow="ellipsis">
          <Typography variant="body2" noWrap title={new_url}>
            {new_url}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Status',
      rowContent: ({ success, error }) =>
        success ? (
          <Chip
            label="Success"
            color="success"
            size="small"
            icon={<CheckCircle />}
          />
        ) : (
          <Chip
            label={error || 'Failed'}
            color="error"
            size="small"
            icon={<Error />}
          />
        ),
    },
  ]

  return (
    <Box padding="10px">
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Leaf Changes ({leafChanges.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table title="" items={leafChanges} columns={columns} />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
